import type { BookingTicket } from '~/types/booking'
import { formatAppointmentCodeFa } from '~/utils/booking/ticketCode'
import { TICKET_SVG_WIDTH, buildTicketRows } from '~/utils/booking/ticketSvg'

export const TICKET_FONT_FAMILY = 'IranYekan, Vazirmatn, Tahoma, sans-serif'

const PAPER = '#faf8f2'
const INK = '#2f3140'
const MUTED = '#6b6e7c'
const LINE = '#d9d4c8'
const ACCENT = '#2563eb'
const PAD_X = 56
const HEADER_H = 210
const ROW_H = 38
const FOOTER_H = 90
const EDGE_H = 12

function truncate(value: string, max = 42): string {
  const text = value.trim()
  if (text.length <= max) return text
  return `${text.slice(0, max - 1)}…`
}

export function ticketCanvasSize(ticket: BookingTicket) {
  const rows = buildTicketRows(ticket)
  const height = HEADER_H + rows.length * ROW_H + FOOTER_H + EDGE_H
  return { width: TICKET_SVG_WIDTH, height, rows }
}

export async function ensureTicketFonts() {
  if (!import.meta.client || !document.fonts?.load) return
  const family = TICKET_FONT_FAMILY
  await Promise.all([
    document.fonts.load(`400 14px ${family}`),
    document.fonts.load(`600 16px ${family}`),
    document.fonts.load(`700 16px ${family}`),
    document.fonts.load(`700 22px ${family}`),
    document.fonts.load(`700 28px ${family}`),
  ])
  await Promise.race([
    document.fonts.ready,
    new Promise<void>((resolve) => {
      window.setTimeout(resolve, 1500)
    }),
  ])
}

function dashedLine(ctx: CanvasRenderingContext2D, y: number, width: number) {
  ctx.save()
  ctx.strokeStyle = LINE
  ctx.lineWidth = 1
  ctx.setLineDash([6, 6])
  ctx.beginPath()
  ctx.moveTo(PAD_X, y)
  ctx.lineTo(width - PAD_X, y)
  ctx.stroke()
  ctx.restore()
}

function drawScallopedEdge(ctx: CanvasRenderingContext2D, y: number, width: number) {
  const step = 12
  const radius = 6
  ctx.fillStyle = PAPER
  ctx.fillRect(0, y, width, EDGE_H)
  ctx.fillStyle = '#ebe6da'
  for (let x = 0; x < width + step; x += step) {
    ctx.beginPath()
    ctx.arc(x + radius, y + EDGE_H, radius, 0, Math.PI, false)
    ctx.fill()
  }
}

export function renderTicketCanvas(
  ticket: BookingTicket,
  scale = 2,
): HTMLCanvasElement {
  const { width, height, rows } = ticketCanvasSize(ticket)
  const canvas = document.createElement('canvas')
  canvas.width = width * scale
  canvas.height = height * scale
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas')

  ctx.scale(scale, scale)
  ctx.fillStyle = PAPER
  ctx.fillRect(0, 0, width, height)

  // Soft shadow under the ticket body.
  ctx.save()
  ctx.shadowColor = 'rgba(15, 23, 42, 0.14)'
  ctx.shadowBlur = 18
  ctx.shadowOffsetY = 8
  ctx.fillStyle = PAPER
  ctx.fillRect(PAD_X - 8, 8, width - (PAD_X - 8) * 2, height - EDGE_H - 8)
  ctx.restore()

  const siteTitle = (ticket.siteTitle || 'نوبت').trim() || 'نوبت'
  const centerX = width / 2
  const right = width - PAD_X

  ctx.textBaseline = 'alphabetic'
  ctx.direction = 'rtl'

  ctx.textAlign = 'center'
  ctx.fillStyle = MUTED
  ctx.font = `700 14px ${TICKET_FONT_FAMILY}`
  ctx.fillText(siteTitle, centerX, 56)

  ctx.fillStyle = INK
  ctx.font = `700 22px ${TICKET_FONT_FAMILY}`
  ctx.fillText('تیکت نوبت', centerX, 92)

  ctx.fillStyle = ACCENT
  ctx.font = `700 28px ${TICKET_FONT_FAMILY}`
  ctx.fillText(formatAppointmentCodeFa(ticket.code), centerX, 148)

  ctx.fillStyle = MUTED
  ctx.font = `400 13px ${TICKET_FONT_FAMILY}`
  ctx.fillText('شماره پیگیری', centerX, 174)

  dashedLine(ctx, 196, width)

  rows.forEach((row, index) => {
    const y = HEADER_H + index * ROW_H + 4
    ctx.textAlign = 'end'
    ctx.fillStyle = MUTED
    ctx.font = `400 16px ${TICKET_FONT_FAMILY}`
    ctx.fillText(row.label, right, y)

    ctx.textAlign = 'start'
    ctx.fillStyle = INK
    ctx.font = `600 16px ${TICKET_FONT_FAMILY}`
    ctx.fillText(truncate(row.value), PAD_X, y)
  })

  const footerLineY = HEADER_H + rows.length * ROW_H - 10
  dashedLine(ctx, footerLineY, width)

  ctx.textAlign = 'center'
  ctx.fillStyle = MUTED
  ctx.font = `400 14px ${TICKET_FONT_FAMILY}`
  ctx.fillText('این تیکت را هنگام مراجعه همراه داشته باشید', centerX, height - EDGE_H - 28)

  drawScallopedEdge(ctx, height - EDGE_H, width)

  return canvas
}
